import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Boxes,
  Eye,
  EyeOff,
  FolderInput,
  ImageOff,
  Images,
  Loader2,
  Pencil,
  Plus,
  RotateCcw,
  Store,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { PageShell, Pagination, StatCard, fmtNum } from '../../Integrations/integrationsUi';
import { Card, EmptyRow, ErrorNotice, INPUT_CLASS, Th } from '../../Scheduler/schedulerUi';
import {
  BrowseFilters,
  BulkAction,
  CatalogueModel,
  DEFAULT_FILTERS,
  errorText,
  useBrowse,
  useCall,
  useCatalogueWrite,
  useCategories,
  useStats,
} from './api';
import { ModelEditor } from './ModelEditor';
import { RowMenu, SOURCE_LABEL, SourceBadge, TrendCard, fmtDay, useConfirm } from './ui';

const BREADCRUMBS = [{ label: 'Stores', href: '/admin/stores' }, { label: 'Product Catalogue' }];

const FILTER_KEYS: (keyof BrowseFilters)[] = ['q', 'etid', 'source', 'photos', 'visibility', 'status', 'kind', 'sort'];

/** Filters live in the URL so a filtered view can be shared or reloaded. */
const useUrlFilters = (): [BrowseFilters, (patch: Partial<BrowseFilters>) => void] => {
  const [params, setParams] = useSearchParams();
  const filters: BrowseFilters = {
    ...DEFAULT_FILTERS,
    ...Object.fromEntries(FILTER_KEYS.filter((k) => params.get(k) != null).map((k) => [k, params.get(k) as string])),
    page: Number(params.get('page')) || 1,
    perPage: Number(params.get('per_page')) || DEFAULT_FILTERS.perPage,
  };
  const set = (patch: Partial<BrowseFilters>) => {
    const next = { ...filters, ...patch };
    if (!('page' in patch)) next.page = 1;
    const p = new URLSearchParams();
    FILTER_KEYS.forEach((k) => {
      const v = String(next[k] ?? '');
      if (v && v !== String(DEFAULT_FILTERS[k])) p.set(k, v);
    });
    if (next.page > 1) p.set('page', String(next.page));
    if (next.perPage !== DEFAULT_FILTERS.perPage) p.set('per_page', String(next.perPage));
    const open = params.get('model');
    if (open) p.set('model', open);
    setParams(p, { replace: true });
  };
  return [filters, set];
};

const NewModelDialog: React.FC<{ open: boolean; onClose: () => void; onCreated: (eid: number) => void }> = ({
  open,
  onClose,
  onCreated,
}) => {
  const categories = useCategories();
  const call = useCall();
  const [name, setName] = useState('');
  const [etid, setEtid] = useState('');
  const [busy, setBusy] = useState(false);
  const [dupe, setDupe] = useState<{ eid: number; name: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (open) {
      setName('');
      setEtid('');
      setDupe(null);
      setErr(null);
    }
  }, [open]);
  const create = async (allowDuplicate = false) => {
    setBusy(true);
    setErr(null);
    try {
      const res = await call<{ eid: number }>('', 'POST', {
        name: name.trim(),
        etid: Number(etid),
        allow_duplicate: allowDuplicate ? 1 : 0,
      });
      onCreated(res.eid);
    } catch (e: any) {
      const existing = e?.data?.existing || e?.data?.data?.existing;
      if (existing?.eid) setDupe({ eid: existing.eid, name: existing.name || existing.title });
      else setErr(errorText(e, 'The model was not created.'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New catalogue model</DialogTitle>
          <DialogDescription>
            Start with a name and category; add specs and photos in the editor that opens next.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <input
            autoFocus
            className={`${INPUT_CLASS} w-full`}
            placeholder="Product name, e.g. Samsung Galaxy A16 Black"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setDupe(null);
            }}
          />
          <select
            className={`${INPUT_CLASS} w-full`}
            value={etid}
            onChange={(e) => {
              setEtid(e.target.value);
              setDupe(null);
            }}
          >
            <option value="">{categories.isLoading ? 'Loading categories…' : 'Choose a category'}</option>
            {(categories.data || []).map((c) => (
              <option key={c.etid} value={c.etid}>
                {c.path}
              </option>
            ))}
          </select>
          {dupe && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              This looks like an existing model: <strong>{dupe.name}</strong> (eid {dupe.eid}).
              <div className="mt-2 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => onCreated(dupe.eid)}>
                  Open existing
                </Button>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => create(true)}>
                  Create anyway
                </Button>
              </div>
            </div>
          )}
          {err && <p className="text-sm text-red-600">{err}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button disabled={!name.trim() || !etid || busy} onClick={() => create(false)}>
            {busy && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ProductCataloguePage: React.FC = () => {
  useBreadcrumbs(BREADCRUMBS);
  const [filters, setFilters] = useUrlFilters();
  const [params, setParams] = useSearchParams();
  const openEid = Number(params.get('model')) || null;
  const setOpen = (eid: number | null) => {
    const p = new URLSearchParams(params);
    if (eid) p.set('model', String(eid));
    else p.delete('model');
    setParams(p, { replace: true });
  };

  const stats = useStats();
  const list = useBrowse(filters);
  const categories = useCategories();
  const { ask, dialog } = useConfirm();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkEtid, setBulkEtid] = useState('');
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState(filters.q);
  useEffect(() => setSearch(filters.q), [filters.q]);
  useEffect(() => {
    const t = setTimeout(() => search !== filters.q && setFilters({ q: search }), 350);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => setSelected(new Set()), [list.data?.page, filters.q, filters.etid, filters.status]);

  const bulk = useCatalogueWrite(
    (call, v: { action: BulkAction; eids: number[]; etid?: number }) => call('/bulk', 'POST', v),
    (v, res) => `${res?.updated ?? 0} of ${v.eids.length} model(s) updated.`
  );
  const single = useCatalogueWrite(
    (call, v: { eid: number; op: 'retire' | 'restore' | 'public' | 'private' }) =>
      v.op === 'retire'
        ? call(`/${v.eid}`, 'DELETE')
        : v.op === 'restore'
          ? call(`/${v.eid}/restore`, 'POST')
          : call(`/${v.eid}`, 'PUT', { private: v.op === 'private' ? 1 : 0 }),
    (v) => ({ retire: 'Model retired.', restore: 'Model restored.', public: 'Model is now public.', private: 'Model is now private.' })[v.op]
  );

  const d = list.data;
  const rows = d?.models || [];
  const pages = d ? Math.max(1, Math.ceil(d.total / d.per_page)) : 1;
  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r.eid));
  const toggle = (eid: number) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(eid) ? n.delete(eid) : n.add(eid);
      return n;
    });

  const runBulk = (action: BulkAction, label: string, danger = false) => {
    const eids = Array.from(selected);
    const etid = action === 'set_category' ? Number(bulkEtid) : undefined;
    const target = categories.data?.find((c) => c.etid === etid);
    ask({
      title: `${label}: ${eids.length} model(s)?`,
      body:
        action === 'retire'
          ? 'Sellers will no longer find them. Listings already made from them are not touched; you can restore them later.'
          : action === 'set_category'
            ? `They move to ${target?.path || 'the chosen category'}. New listings cloned from them land there too.`
            : action === 'make_private'
              ? 'Only each model’s owner will see it in the picker.'
              : action === 'make_public'
                ? 'Every seller will be able to find and list them.'
                : 'They go back into the seller picker.',
      confirm: label,
      danger,
      run: () => bulk.mutateAsync({ action, eids, etid }, { onSuccess: () => setSelected(new Set()) }),
    });
  };

  const rowEntries = (m: CatalogueModel) => [
    { label: 'Open editor', icon: <Pencil className="h-4 w-4" />, onSelect: () => setOpen(m.eid) },
    {
      label: m.private ? 'Make public' : 'Make private',
      icon: m.private ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />,
      onSelect: () =>
        ask({
          title: `${m.private ? 'Make public' : 'Make private'}: ${m.name}?`,
          body: m.private ? 'Every seller will be able to find and list it.' : 'Only its owner will see it in the picker.',
          confirm: m.private ? 'Make public' : 'Make private',
          run: () => single.mutateAsync({ eid: m.eid, op: m.private ? 'public' : 'private' }),
        }),
    },
    {
      label: 'Restore',
      icon: <RotateCcw className="h-4 w-4" />,
      hidden: !m.deleted,
      onSelect: () =>
        ask({ title: `Restore ${m.name}?`, body: 'It goes back into the seller picker.', confirm: 'Restore', run: () => single.mutateAsync({ eid: m.eid, op: 'restore' }) }),
    },
    {
      label: 'Retire',
      icon: <Trash2 className="h-4 w-4" />,
      danger: true,
      separatorBefore: true,
      hidden: !!m.deleted,
      onSelect: () =>
        ask({
          title: `Retire ${m.name}?`,
          body: `Sellers will no longer find it. ${m.usage} listing(s) made from it are not touched. You can restore it later.`,
          confirm: 'Retire',
          danger: true,
          run: () => single.mutateAsync({ eid: m.eid, op: 'retire' }),
        }),
    },
  ];

  const s = stats.data;
  const loadingStat = stats.isLoading ? '…' : '—';
  const sourceOptions = useMemo(() => s?.by_source.map((b) => b.source) || Object.keys(SOURCE_LABEL), [s]);

  return (
    <PageShell
      title="Product Catalogue"
      description="The ready-made products sellers pick from when they add a listing. Every model here is cloned into a listing with its name, category, specs and photos; edits apply to listings made afterwards."
      actions={
        <Button onClick={() => setCreating(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New model
        </Button>
      }
    >
      {stats.isError && <ErrorNotice error={stats.error} onRetry={() => stats.refetch()} />}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <StatCard
          label="Models"
          value={s ? fmtNum(s.total) : loadingStat}
          hint={s ? `${fmtNum(s.public)} public, ${fmtNum(s.private)} private` : 'Loading catalogue totals'}
          icon={<Boxes className="h-5 w-5 text-blue-900" />}
          color="bg-blue-50"
        />
        <button type="button" className="text-left" onClick={() => setFilters({ photos: 'with' })}>
          <StatCard
            label="With photos"
            value={s ? fmtNum(s.with_photos) : loadingStat}
            hint={s && s.total ? `${Math.round((100 * s.with_photos) / s.total)}% of models` : undefined}
            icon={<Images className="h-5 w-5 text-emerald-700" />}
            color="bg-emerald-50"
          />
        </button>
        <button type="button" className="text-left" onClick={() => setFilters({ photos: 'without' })}>
          <StatCard
            label="Need a photo"
            value={s ? fmtNum(s.without_photos) : loadingStat}
            hint="Click to work through them"
            icon={<ImageOff className="h-5 w-5 text-amber-700" />}
            color="bg-amber-50"
          />
        </button>
        <button type="button" className="text-left" onClick={() => setFilters({ sort: 'usage' })}>
          <StatCard
            label="Listed by sellers"
            value={s ? fmtNum(s.listed_by_sellers) : loadingStat}
            hint="Models with at least one listing"
            icon={<Store className="h-5 w-5 text-violet-700" />}
            color="bg-violet-50"
          />
        </button>
        <button type="button" className="text-left" onClick={() => setFilters({ status: 'retired' })}>
          <StatCard
            label="Retired"
            value={s ? fmtNum(s.retired) : loadingStat}
            hint="Hidden from sellers, restorable"
            icon={<Trash2 className="h-5 w-5 text-gray-600" />}
            color="bg-gray-100"
          />
        </button>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <TrendCard label="Models added per week" points={s?.added_per_week} hint="Last 12 weeks" color="#1e3a8a" loading={stats.isLoading} />
        <TrendCard
          label="Listings made from the catalogue per week"
          points={s?.listings_per_week}
          hint="Seller listings cloned from a model"
          color="#047857"
          loading={stats.isLoading}
        />
      </div>

      <Card
        title="Models"
        note={d ? `${fmtNum(d.total)} match` : list.isLoading ? 'Loading…' : undefined}
        flush
      >
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-4 py-3">
          <input
            className={`${INPUT_CLASS} min-w-[14rem] flex-1`}
            placeholder="Search name, barcode or eid"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className={`${INPUT_CLASS} max-w-[18rem]`}
            value={filters.etid}
            onChange={(e) => setFilters({ etid: e.target.value })}
            aria-label="Category"
          >
            <option value="">All categories</option>
            {(categories.data || [])
              .filter((c) => c.models > 0 || String(c.etid) === filters.etid)
              .map((c) => (
                <option key={c.etid} value={c.etid}>
                  {c.path} ({c.models})
                </option>
              ))}
          </select>
          <select className={INPUT_CLASS} value={filters.source} onChange={(e) => setFilters({ source: e.target.value })} aria-label="Source">
            <option value="">All sources</option>
            {sourceOptions.map((src) => (
              <option key={src} value={src}>
                {SOURCE_LABEL[src] || src}
              </option>
            ))}
          </select>
          <select className={INPUT_CLASS} value={filters.photos} onChange={(e) => setFilters({ photos: e.target.value })} aria-label="Photos">
            <option value="">Any photos</option>
            <option value="with">Has photos</option>
            <option value="without">No photos</option>
          </select>
          <select className={INPUT_CLASS} value={filters.visibility} onChange={(e) => setFilters({ visibility: e.target.value })} aria-label="Visibility">
            <option value="all">Public and private</option>
            <option value="public">Public only</option>
            <option value="private">Private only</option>
          </select>
          <select className={INPUT_CLASS} value={filters.status} onChange={(e) => setFilters({ status: e.target.value })} aria-label="Status">
            <option value="active">Active</option>
            <option value="retired">Retired</option>
            <option value="all">Active and retired</option>
          </select>
          <select className={INPUT_CLASS} value={filters.kind} onChange={(e) => setFilters({ kind: e.target.value })} aria-label="Kind">
            <option value="">Every model</option>
            <option value="device">Phones and tablets (device specs)</option>
          </select>
          <select className={INPUT_CLASS} value={filters.sort} onChange={(e) => setFilters({ sort: e.target.value })} aria-label="Sort">
            <option value="recent">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="name">Name</option>
            <option value="usage">Most listed</option>
          </select>
        </div>

        {selected.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 border-b border-blue-100 bg-blue-50 px-4 py-2 text-sm">
            <span className="font-medium text-gray-800">{selected.size} selected</span>
            <Button size="sm" variant="outline" onClick={() => runBulk('make_public', 'Make public')}>
              <Eye className="mr-1.5 h-3.5 w-3.5" /> Make public
            </Button>
            <Button size="sm" variant="outline" onClick={() => runBulk('make_private', 'Make private')}>
              <EyeOff className="mr-1.5 h-3.5 w-3.5" /> Make private
            </Button>
            <span className="flex items-center gap-1">
              <select className={`${INPUT_CLASS} h-8 max-w-[16rem]`} value={bulkEtid} onChange={(e) => setBulkEtid(e.target.value)} aria-label="Move to category">
                <option value="">Move to category…</option>
                {(categories.data || []).map((c) => (
                  <option key={c.etid} value={c.etid}>
                    {c.path}
                  </option>
                ))}
              </select>
              <Button size="sm" variant="outline" disabled={!bulkEtid} onClick={() => runBulk('set_category', 'Move')}>
                <FolderInput className="mr-1.5 h-3.5 w-3.5" /> Move
              </Button>
            </span>
            {filters.status === 'retired' ? (
              <Button size="sm" variant="outline" onClick={() => runBulk('restore', 'Restore')}>
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Restore
              </Button>
            ) : (
              <Button size="sm" variant="outline" className="text-red-700" onClick={() => runBulk('retire', 'Retire', true)}>
                <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Retire
              </Button>
            )}
            <button type="button" className="ml-auto text-gray-500 hover:text-gray-800" onClick={() => setSelected(new Set())}>
              Clear
            </button>
          </div>
        )}

        {list.isError ? (
          <div className="p-4">
            <ErrorNotice error={list.error} onRetry={() => list.refetch()} />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50 text-left text-xs text-gray-500">
                <tr>
                  <th className="w-10 px-4 py-2">
                    <input
                      type="checkbox"
                      aria-label="Select all on this page"
                      checked={allOnPage}
                      onChange={() =>
                        setSelected((cur) => {
                          const n = new Set(cur);
                          rows.forEach((r) => (allOnPage ? n.delete(r.eid) : n.add(r.eid)));
                          return n;
                        })
                      }
                    />
                  </th>
                  <Th>Model</Th>
                  <Th>Category</Th>
                  <Th>Source</Th>
                  <Th right>Photos</Th>
                  <Th right>Listings</Th>
                  <Th>Added</Th>
                  <Th />
                </tr>
              </thead>
              <tbody className={`divide-y divide-gray-50 ${list.isFetching && !list.isLoading ? 'opacity-60' : ''}`}>
                {rows.length === 0 ? (
                  <EmptyRow cols={8} loading={list.isLoading}>
                    No models match these filters.
                  </EmptyRow>
                ) : (
                  rows.map((m) => (
                    <tr
                      key={m.eid}
                      onClick={() => setOpen(m.eid)}
                      className={`cursor-pointer hover:bg-gray-50 ${selected.has(m.eid) ? 'bg-blue-50/60' : ''}`}
                    >
                      <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" aria-label={`Select ${m.name}`} checked={selected.has(m.eid)} onChange={() => toggle(m.eid)} />
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-3">
                          {m.image ? (
                            <img src={m.image} alt="" loading="lazy" className="h-10 w-10 shrink-0 rounded bg-white object-contain" />
                          ) : (
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-gray-100">
                              <ImageOff className="h-4 w-4 text-gray-300" />
                            </span>
                          )}
                          <div className="min-w-0">
                            <p className="truncate font-medium text-gray-900">{m.name}</p>
                            <p className="text-xs text-gray-400">
                              eid {m.eid}
                              {m.gtin ? ` · ${m.gtin}` : ''}
                              {m.deleted ? ' · retired' : ''}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-gray-600">{m.category || `Type ${m.etid}`}</td>
                      <td className="px-4 py-2">
                        <SourceBadge source={m.source} isPrivate={!!m.private} />
                      </td>
                      <td className={`px-4 py-2 text-right ${m.photos ? 'text-gray-700' : 'text-amber-700'}`}>{m.photos || 'none'}</td>
                      <td className="px-4 py-2 text-right text-gray-700">{m.usage}</td>
                      <td className="px-4 py-2 text-gray-500">{fmtDay(m.date_added)}</td>
                      <td className="px-2 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                        <RowMenu label={`Actions for ${m.name}`} entries={rowEntries(m)} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        {d && (
          <Pagination
            page={d.page}
            pages={pages}
            total={d.total}
            perPage={d.per_page}
            onPage={(page) => setFilters({ page })}
            onPerPage={(perPage) => setFilters({ perPage })}
          />
        )}
      </Card>

      {s && s.by_category.length > 0 && (
        <Card title="Coverage by category" note="Click a row to filter">
          <div className="grid gap-x-6 gap-y-1 p-4 sm:grid-cols-2 lg:grid-cols-3">
            {s.by_category.map((c) => {
              const pct = c.count ? Math.round((100 * c.with_photos) / c.count) : 0;
              return (
                <button
                  key={c.etid}
                  type="button"
                  onClick={() => setFilters({ etid: String(c.etid) })}
                  className="flex items-center gap-2 rounded px-1 py-1 text-left text-sm hover:bg-gray-50"
                >
                  <span className="min-w-0 flex-1 truncate text-gray-700">{c.category || `Type ${c.etid}`}</span>
                  <span className="w-12 text-right text-gray-500">{fmtNum(c.count)}</span>
                  <span className="h-1.5 w-16 overflow-hidden rounded bg-gray-100" title={`${pct}% have photos`}>
                    <span className="block h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                  </span>
                  <span className="w-9 text-right text-xs text-gray-400">{pct}%</span>
                </button>
              );
            })}
          </div>
        </Card>
      )}

      <ModelEditor eid={openEid} onClose={() => setOpen(null)} />
      <NewModelDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(eid) => {
          setCreating(false);
          setOpen(eid);
        }}
      />
      {dialog}
    </PageShell>
  );
};

export default ProductCataloguePage;
